import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useEffectEvent } from 'react';

export interface SnackbarMessage {
  /** Nowe id = nowy komunikat (i nowe odliczanie). */
  id: number;
  text: string;
  action?: { label: string; onClick: () => void };
}

interface Props {
  message: SnackbarMessage | null;
  onDismiss: () => void;
  duration?: number;
  /** Dół: moje akcje (Cofnij); góra: wieści od domowników — nie zasłaniają sobie nawzajem. */
  placement?: 'bottom' | 'top';
  /** Odległość od krawędzi ekranu (dół: nad paskiem akcji, góra: pod nagłówkiem). */
  offset?: string;
}

const DEFAULT_OFFSET = {
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)',
  top: 'calc(env(safe-area-inset-top, 0px) + 4.5rem)',
};

/** Komunikat z opcjonalną akcją (np. „Cofnij”); znika sam po kilku sekundach. */
export function Snackbar({ message, onDismiss, duration = 5000, placement = 'bottom', offset = DEFAULT_OFFSET[placement] }: Props) {
  const dismiss = useEffectEvent(onDismiss);
  const id = message?.id;
  useEffect(() => {
    if (id === undefined) return;
    const t = window.setTimeout(dismiss, duration);
    return () => window.clearTimeout(t);
  }, [id, duration]);

  const from = placement === 'bottom' ? 16 : -16;
  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40 mx-auto max-w-xl px-3"
      style={placement === 'bottom' ? { bottom: offset } : { top: offset }}
      role="status"
      aria-live="polite"
    >
      <AnimatePresence>
        {message && (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: from }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: from / 2 }}
            transition={{ duration: 0.15 }}
            className="pointer-events-auto flex min-h-12 items-center gap-2 rounded-xl bg-stone-900 py-1 pr-1 pl-4 text-white shadow-lg dark:bg-stone-100 dark:text-stone-900"
          >
            <span className="line-clamp-2 min-w-0 flex-1 py-1">{message.text}</span>
            {message.action && (
              <button
                type="button"
                onClick={() => {
                  message.action?.onClick();
                  onDismiss();
                }}
                className="min-h-11 shrink-0 rounded-lg px-3 font-semibold text-brand-500 active:bg-white/10 dark:text-brand-700 dark:active:bg-black/10"
              >
                {message.action.label}
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
